import type { Pasta } from './api';

/**
 * Rótulo de cada pasta na barra lateral.
 *
 * O nome-folha basta na maioria das caixas, mas servidor que expõe o maildir
 * dentro da INBOX produz coisas como `INBOX.spam` e
 * `INBOX.mail.virtual.contato@empresa_com.spam` - duas entradas escritas
 * "spam", indistinguíveis. Aqui o caminho aparece só onde precisa aparecer:
 * quem não colide continua com o nome curto.
 *
 * Esconder pasta seria a outra saída, e é pior: pasta que o servidor mostra e o
 * cliente some é a classe de problema mais difícil de perceber.
 */

/**
 * O separador vem da API desde o ADR 0045, gravado do LIST do servidor. Sem
 * ele - cache de versão anterior, pasta que o sync ainda não relistou -, deduz
 * do próprio caminho: é o que está logo antes do nome-folha. Cobre `.` do
 * Dovecot e `/` da maioria.
 */
function separador(pasta: Pasta): string | null {
  if (pasta.delimiter != null) return pasta.delimiter;
  const i = pasta.path.toLowerCase().lastIndexOf(pasta.name.toLowerCase());
  return i > 0 ? (pasta.path[i - 1] ?? null) : null;
}

function segmentos(pasta: Pasta): string[] {
  const sep = separador(pasta);
  return sep === null ? [pasta.name] : pasta.path.split(sep).filter((s) => s !== '');
}

/** Os `n` últimos segmentos, que é o mínimo de contexto que distingue. */
function cauda(partes: string[], n: number): string {
  return partes.slice(Math.max(0, partes.length - n)).join('/');
}

export interface PastaRotulada {
  pasta: Pasta;
  /** O rotulo inteiro, com o contexto que desambigua. */
  rotulo: string;
  /**
   * O rotulo partido em duas partes, porque a barra lateral e estreita e o
   * corte do CSS come o fim - que e justamente o que distingue.
   *
   * Numa caixa de cPanel de verdade apareceram tres pastas assim:
   * `INBOX.mail.virtual.contato@dominio_com`, a mesma com `.sent` e a mesma com
   * `.spam`. Os tres rotulos comecavam com o mesmo endereco de 22 caracteres e
   * terminavam em `/sent` e `/spam`: na tela, tres entradas identicas
   * ("contato@bancodaweb..."). Com as partes separadas, o `prefixo` encolhe e a
   * `folha` fica inteira - "contato@ban.../sent" e "contato@ban.../spam".
   */
  prefixo: string;
  folha: string;
  /** Caminho completo, para o `title` de quem ficou truncado. */
  caminho: string;
}

/** Parte o rotulo no ultimo separador: o que pode encolher e o que nao pode. */
function partir(rotulo: string): { prefixo: string; folha: string } {
  const corte = rotulo.lastIndexOf('/');
  return corte === -1
    ? { prefixo: '', folha: rotulo }
    : { prefixo: rotulo.slice(0, corte + 1), folha: rotulo.slice(corte + 1) };
}

/** Nome em pt-BR das pastas de papel conhecido (Secao 6.11), como no Outlook. */
const NOMES_DE_PAPEL: Record<string, string> = {
  inbox: 'Entrada',
  drafts: 'Rascunhos',
  sent: 'Enviados',
  archive: 'Arquivo',
  junk: 'Spam',
  trash: 'Lixeira',
};

function profundidade(pasta: Pasta): number {
  return segmentos(pasta).length;
}

/**
 * A pasta principal de cada papel: a de caminho mais curto. So ela ganha o
 * nome em portugues - `INBOX.Sent` vira "Enviados", e a `sent` de dentro do
 * endereco virtual continua com o contexto que a distingue. Empate na mesma
 * profundidade (`INBOX.Junk` e `INBOX.spam`, as duas `junk`) fica sem
 * principal: traduzir as duas daria dois "Spam" indistinguiveis.
 */
function principaisPorPapel(pastas: Pasta[]): Set<string> {
  const porPapel = new Map<string, Pasta[]>();
  for (const p of pastas) {
    if (p.role === null || NOMES_DE_PAPEL[p.role] === undefined) continue;
    porPapel.set(p.role, [...(porPapel.get(p.role) ?? []), p]);
  }
  const principais = new Set<string>();
  for (const grupo of porPapel.values()) {
    const menor = Math.min(...grupo.map(profundidade));
    const rasas = grupo.filter((p) => profundidade(p) === menor);
    if (rasas.length === 1) principais.add(rasas[0]!.id);
  }
  return principais;
}

/**
 * Nome de cada pasta numa lista em arvore, onde o recuo ja da o contexto: a
 * principal de cada papel em pt-BR (`INBOX` vira "Entrada"), as outras com o
 * nome-folha. Quem mostra o nome traduzido mostra o caminho real junto.
 */
export function nomesNaArvore(pastas: Pasta[]): Map<string, string> {
  const principais = principaisPorPapel(pastas);
  return new Map(
    pastas.map((p) => [p.id, principais.has(p.id) ? NOMES_DE_PAPEL[p.role!]! : p.name]),
  );
}

export function rotularPastas(pastas: Pasta[]): PastaRotulada[] {
  const principais = principaisPorPapel(pastas);
  // A principal sai da contagem: com nome proprio, ela nao colide com ninguem.
  const quantos = new Map<string, number>();
  for (const p of pastas) {
    if (principais.has(p.id)) continue;
    const chave = p.name.toLowerCase();
    quantos.set(chave, (quantos.get(chave) ?? 0) + 1);
  }

  return pastas.map((pasta) => {
    const base = { pasta, caminho: pasta.path };
    if (principais.has(pasta.id)) {
      const nome = NOMES_DE_PAPEL[pasta.role!]!;
      return { ...base, rotulo: nome, prefixo: '', folha: nome };
    }
    if ((quantos.get(pasta.name.toLowerCase()) ?? 0) < 2) {
      return { ...base, rotulo: pasta.name, ...partir(pasta.name) };
    }

    const partes = segmentos(pasta);

    // Cresce o contexto até o rótulo ficar único entre as pastas que colidem.
    for (let n = 2; n <= partes.length; n++) {
      const candidato = cauda(partes, n);
      const iguais = pastas.filter(
        (outra) =>
          !principais.has(outra.id) &&
          outra.name.toLowerCase() === pasta.name.toLowerCase() &&
          cauda(segmentos(outra), n).toLowerCase() === candidato.toLowerCase(),
      );
      if (iguais.length === 1) return { ...base, rotulo: candidato, ...partir(candidato) };
    }

    // Caminhos idênticos não existem (a chave é única por conta), mas se o
    // servidor inventar, o caminho inteiro ainda é a melhor resposta.
    const inteiro = partes.join('/');
    return { ...base, rotulo: inteiro, ...partir(inteiro) };
  });
}

// Árvore (ADR 0045) -----------------------------------------------------------

export interface NoDaArvore {
  pasta: Pasta;
  /** 0 na raiz. */
  nivel: number;
  paiId: string | null;
}

/**
 * A pai de uma pasta é a mais próxima acima dela que existe na lista. "Mais
 * próxima" porque o servidor pode ter nível intermediário `\Noselect`, que o
 * sync não guarda: `a.b.c` sem `a.b` pendura em `a`.
 */
function paiDe(pasta: Pasta, porCaminho: Map<string, Pasta>): Pasta | null {
  const sep = separador(pasta);
  if (sep === null) return null;
  let caminho = pasta.path;
  for (;;) {
    const corte = caminho.lastIndexOf(sep);
    if (corte <= 0) return null;
    caminho = caminho.slice(0, corte);
    const pai = porCaminho.get(caminho);
    if (pai !== undefined) return pai;
  }
}

/** Entrada primeiro; o resto em ordem alfabética, que o servidor não garante. */
function ordem(a: Pasta, b: Pasta): number {
  if (a.role === 'inbox') return -1;
  if (b.role === 'inbox') return 1;
  return a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' });
}

/** A lista em ordem de árvore, cada pasta logo depois da pai. */
export function arvoreDePastas(pastas: Pasta[]): NoDaArvore[] {
  const porCaminho = new Map(pastas.map((p) => [p.path, p]));
  const filhas = new Map<string | null, Pasta[]>();
  for (const p of pastas) {
    const pai = paiDe(p, porCaminho)?.id ?? null;
    filhas.set(pai, [...(filhas.get(pai) ?? []), p]);
  }

  const saida: NoDaArvore[] = [];
  const visitar = (paiId: string | null, nivel: number) => {
    for (const p of [...(filhas.get(paiId) ?? [])].sort(ordem)) {
      saida.push({ pasta: p, nivel, paiId });
      visitar(p.id, nivel + 1);
    }
  };
  visitar(null, 0);
  return saida;
}

/** A pasta e tudo que está dentro dela: para onde ela não pode ser movida. */
export function idsDaSubarvore(arvore: NoDaArvore[], id: string): Set<string> {
  const ids = new Set([id]);
  // A ordem de árvore garante que a pai aparece antes das filhas.
  for (const no of arvore) if (no.paiId !== null && ids.has(no.paiId)) ids.add(no.pasta.id);
  return ids;
}

/** Pasta que o resto do sistema usa por papel: a API recusa mexer nela. */
export function ehDeSistema(pasta: Pasta): boolean {
  return pasta.role != null || pasta.detectedRole != null || pasta.path.toUpperCase() === 'INBOX';
}
